import React, { useEffect, useRef, useState } from "react";
import { FilterDrawer } from "landing-page/views/filter-drawer";
import { BaseFilter } from "landing-page/views/filter-bar/FilterBar";

import { Icon } from "@client:common/icon";
import outsideClickListener from "@client:common/outsideClickListener";
import { Checkbox, CheckboxGroup, FormControl } from "@client:common/form";
import { Button, IconButton } from "@client:common/button";
import { Ripple } from "@client:common/effects/ripple";
import { Backdrop } from "@client:common/overlay/backdrop";

import "./filterDropdown.light.scss";
import "./filterDropdown.dark.scss";

interface Props {
  title: string;
  placeholder: string;
  filters: {
    info?: Array<{ id: number; title: string; description: string }>;
    filters: Array<{ id: number; value: string }>;
  };
  selected: BaseFilter;
  setSelected: (value: BaseFilter) => void;
  applyFilter: (selectedValues: any, group: string) => void;
  group: string;
}

function FilterDropdown(props: Props) {
  const {
    title,
    filters,
    selected,
    setSelected,
    applyFilter,
    group,
    placeholder
  } = props;
  const [listening, setListening] = useState(false);
  const [open, setOpen] = useState(false);
  const [isSelectAll, setIsSelectAll] = useState([]);
  const dropdownRef = useRef(null);
  const toggle = () => setOpen(!open);

  const [drawerOpen, setDrawerOpen] = useState(false);

  const drawerToggleClickHandler = () => {
    setDrawerOpen(!drawerOpen);
  };

  let backdrop;
  if (drawerOpen) {
    backdrop = (
      <Backdrop visible={drawerOpen} onClick={drawerToggleClickHandler} />
    );
  }

  const FilterFormControl = new FormControl(selected?.selected);
  const SelectAllFormControl = new FormControl(isSelectAll);

  useEffect(
    outsideClickListener(listening, setListening, dropdownRef, setOpen)
  );

  useEffect(() => {
    if (
      selected.selectAll === false ||
      (selected.selectAll === true &&
        FilterFormControl.getValue()?.length !== filters.filters.length)
    ) {
      SelectAllFormControl.setValue([]);
      setIsSelectAll(SelectAllFormControl.getValue());
    }
    if (
      (selected.selectAll === false && FilterFormControl.getValue()?.length) ===
      filters.filters.length
    ) {
      SelectAllFormControl.setValue(["Select All"]);
      setIsSelectAll(SelectAllFormControl.getValue());
      setSelected({ ...selected, selectAll: true });
    }
  }, [selected.selectAll, selected]);

  const __handleSelectAll__ = () => {
    if (selected.selectAll === true) {
      FilterFormControl.setValue(undefined);
      SelectAllFormControl.setValue([]);
    } else {
      FilterFormControl.setValue(filters.filters.map((li) => li));
      SelectAllFormControl.setValue(["Select All"]);
    }

    setSelected({
      selected: FilterFormControl.getValue(),
      selectAll: !selected.selectAll
    });
    setIsSelectAll(SelectAllFormControl.getValue());
  };

  const handleFilterClick = () => {
    if (selected.selectAll === true) {
      SelectAllFormControl.setValue([]);
      setIsSelectAll(SelectAllFormControl.getValue());
      setSelected({
        selected: FilterFormControl.getValue(),
        selectAll: !selected.selectAll
      });
    } else {
      setSelected({
        selected: FilterFormControl.getValue(),
        selectAll: selected.selectAll
      });
    }

    // apply filters
    applyFilter(FilterFormControl.getValue(), group);
    setSelected({
      ...selected,
      selected:
        FilterFormControl.getValue()?.length > 0
          ? FilterFormControl.getValue()
          : undefined
    });
  };

  return (
    <>
      {backdrop}
      {filters.info && (
        <FilterDrawer
          show={drawerOpen}
          toggle={drawerToggleClickHandler}
          data={filters}
          filterTitle={title}
        />
      )}
      <div ref={dropdownRef} className="dd-wrapper">
        <div className="dd-header__title-outside">
          <p className="dd-header__title--bold">{title}</p>
          {filters.info && (
            <IconButton
              style="basic"
              color="primary"
              className="help-icon"
              onClick={drawerToggleClickHandler}
            >
              <Icon style="outlined" icon="info" />
            </IconButton>
          )}
        </div>
        <div
          tabIndex={0}
          className={`dd-header ${open ? "active" : ""}`}
          role="button"
          onKeyPress={() => toggle()}
          onClick={() => toggle()}
        >
          <div className="dd-header__title">
            <p className="dd-header__title--bold">
              {selected.selectAll &&
                FilterFormControl.getValue()?.length === filters.filters.length
                ? placeholder
                : FilterFormControl.getValue()?.length > 0
                  ? `${FilterFormControl.getValue()?.length} Selected`
                  : placeholder}
            </p>
          </div>
          <div className="dd-header__action">
            <Icon icon={!open ? "expand_more" : "expand_less"} />
          </div>
        </div>
        {open && (
          <div className="dd-list">
            <CheckboxGroup label="" hint="" formControl={FilterFormControl}>
              {filters.filters.map((item) => (
                <Checkbox
                  key={item.id}
                  label={item.value}
                  name="filters-dropdown"
                  value={item}
                  onChange={handleFilterClick}
                />
              ))}
            </CheckboxGroup>

            <div>
              <Button
                style="basic"
                color="primary"
                onClick={() => {
                  setSelected({ selected: undefined, selectAll: undefined });
                  setIsSelectAll([]);
                  applyFilter([], group);
                  toggle();
                }}
              >
                <Ripple />
                RESET
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default FilterDropdown;
